import React, { useMemo, useState } from "react";

import {
  faCalculatorSimple,
  faMinus,
  faPlus,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import {
  CellContext,
  createColumnHelper,
  RowData,
} from "@tanstack/react-table";

import { HighlightedText } from "src/base-components/HighlightedText";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { FeatureActionsMenu } from "src/featureCatalogue/Features/FeatureActionsMenu";
import {
  EntityFilter,
  EventTypeFilter,
  StatusFilter,
} from "src/featureCatalogue/Features/FilterActions";
import { QueryStatusPill } from "src/featureCatalogue/Queries/QueryStatusPill";
import { useFeatures } from "src/featureCatalogue/queries";
import {
  commonColumns,
  EntityPills,
  Feature,
  statusColumns,
} from "src/featureCatalogue/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { WhitePane } from "src/jobs/common/WhitePane";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { getTypeIcon } from "src/utils/icons";
import { speakPythonPrimitive } from "src/utils/speakPython";

const columnHelper = createColumnHelper<Feature>();

const HighlightedCellText = <D extends RowData>({
  info,
}: {
  info: CellContext<D, string | null | undefined>;
}) => (
  <HighlightedText
    highlight={info.table.getState().globalFilter}
    highlightClassName="font-inter-semibold-12px bg-yellow-200"
    text={info.getValue()}
  />
);

// Shared column definitions
const nameColumn = (headerText: string) =>
  columnHelper.accessor("name", {
    header: (info) => <Header info={info}>{headerText}</Header>,
    cell: (info) => {
      const name = info.getValue();
      return (
        <Cell className="gap-x-0.5" info={info}>
          <Icon
            color="text-gray-500"
            icon={
              info.row.original.feature_type
                ? getTypeIcon(info.row.original.feature_type, faMinus)
                : faMinus
            }
            size="xs"
          />
          <Tooltip placement="top" title={name} asChild>
            <div className="truncate">
              <HighlightedCellText info={info} />
            </div>
          </Tooltip>
        </Cell>
      );
    },
    size: 300,
    maxSize: 300,
    filterFn: "includesString",
    meta: {
      sticky: "left",
    },
  });

const keyColumn = (headerText: string) =>
  columnHelper.accessor("key", {
    header: (info) => <Header info={info}>{headerText}</Header>,
    cell: (info) => (
      <Cell info={info}>
        <Pill size="sm" variant="gray">
          <Pill.Text>
            <HighlightedCellText info={info} />
          </Pill.Text>
        </Pill>
      </Cell>
    ),
    size: 100,
    filterFn: "includesString",
  });

const descriptionColumn = () =>
  columnHelper.accessor("description", {
    header: (info) => <Header info={info}>Description</Header>,
    cell: (info) => (
      <DisplayDescriptionCell info={info} value={info.getValue()} />
    ),
    size: 300,
    maxSize: 300,
    filterFn: "includesString",
  });

const entityTypesColumn = () => {
  return columnHelper.accessor("entity_types", {
    header: (info) => (
      <Header info={info}>
        <div className="flex items-center gap-x-4">
          <span>Entity</span>
          <EntityFilter column={info.column} />
        </div>
      </Header>
    ),
    cell: (info) => (
      <Cell className="flex space-x-2" info={info}>
        <EntityPills entities={info.getValue() ?? []} />
      </Cell>
    ),
    filterFn: "includesString",
  });
};

const featureDefaultColumn = () =>
  columnHelper.accessor("feature_default", {
    header: (info) => (
      <Header info={info}>
        <span className="whitespace-nowrap">Default Value</span>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>{speakPythonPrimitive(info.getValue() ?? "None")}</Cell>
    ),
  });

const actionsMenuColumn = () =>
  columnHelper.display({
    id: "actions-menu",
    header: () => null,
    cell: (info) => (
      <Cell className="justify-end" info={info}>
        <FeatureActionsMenu feature={info.row.original} />
      </Cell>
    ),
    enableGlobalFilter: false,
    meta: {
      sticky: "right",
    },
  });

const getFeatureColumns = () => [
  nameColumn("Feature"),
  keyColumn("Feature Key"),
  descriptionColumn(),
  entityTypesColumn(),
  // Event column
  columnHelper.accessor("query.event" as keyof Feature, {
    header: (info) => (
      <Header info={info}>
        <div className="flex items-center gap-x-4">
          <span>Event</span>
          <EventTypeFilter column={info.column} />
        </div>
      </Header>
    ),
    cell: (info) => {
      const eventColumn = commonColumns<Feature>().find(
        (col) => col.accessorKey === "event",
      );
      return info.row.original.query && eventColumn
        ? eventColumn.cell(info)
        : null;
    },
  }),
  ...statusColumns(columnHelper, {
    getEnvironmentSettings: (row) => row.query?.environment_settings,
    hasQuery: (row) => !!row.query?.id,
  }),
  ...commonColumns<Feature>()
    .filter((column) => column.accessorKey !== "event")
    .map((column) =>
      columnHelper.accessor(`query.${column.accessorKey}` as keyof Feature, {
        header: (info) => column.header(info),
        cell: (info) => (info.row.original.query ? column.cell(info) : null),
        enableGlobalFilter: false,
      }),
    ),
  featureDefaultColumn(),
  actionsMenuColumn(),
];

const getColumns = (env: string) => [
  nameColumn("Feature name"),
  descriptionColumn(),
  keyColumn("Key"),
  columnHelper.accessor(
    (row) => {
      return row.query?.environment_settings?.[env]?.status;
    },
    {
      id: "environment_settings_status",
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-2">
            Status
            <StatusFilter column={info.column} />
          </div>
        </Header>
      ),
      cell: (info) => {
        const currentStatus = info.getValue();

        return currentStatus ? (
          <Cell info={info}>
            <QueryStatusPill status={currentStatus} />
          </Cell>
        ) : null;
      },
      enableGlobalFilter: false,
      filterFn: "equals",
    },
  ),
  featureDefaultColumn(),
  entityTypesColumn(),
  ...commonColumns<Feature>().map((column) =>
    columnHelper.accessor(`query.${column.accessorKey}` as keyof Feature, {
      header: (info) =>
        column.accessorKey === "event" ? (
          <Header info={info}>
            <div className="flex items-center gap-x-2">
              Event
              <EventTypeFilter column={info.column} />
            </div>
          </Header>
        ) : (
          column.header(info)
        ),
      cell: (info) => (info.row.original.query ? column.cell(info) : null),
      enableGlobalFilter: false,
    }),
  ),
  actionsMenuColumn(),
];

export const Features: React.FC<{
  openCreateModal: () => void;
  searchQuery: string;
}> = ({ openCreateModal, searchQuery }) => {
  const [environment] = useEnvironment();
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const { data: features, isLoading } = useFeatures({ environment });
  const columns = useMemo(() => {
    if (featureLifecycleEnabled) {
      return getFeatureColumns();
    }
    return getColumns(environment);
  }, [environment, featureLifecycleEnabled]);

  return (
    <WhitePane>
      <div className="flex h-full w-full flex-col gap-y-4">
        {!isLoading && features?.length === 0 ? (
          <EmptyState
            actionButton={
              !searchQuery
                ? {
                    icon: faPlus,
                    text: "Create Feature",
                    onClick: () => {
                      openCreateModal();
                    },
                  }
                : undefined
            }
            dataLoc="features-empty-state"
            description={
              searchQuery
                ? "Try adjusting your search to find what you're looking for"
                : "Features help you detect patterns and analyze behavior across your entities and events."
            }
            headline={
              searchQuery
                ? "No matching features found"
                : "No features configured yet"
            }
            icon={searchQuery ? faSearch : faCalculatorSimple}
          />
        ) : (
          <div
            className={`${featureLifecycleEnabled ? "relative flex h-full w-full flex-col p-4" : ""}`}
          >
            <TableComp
              columns={columns}
              data={features ?? []}
              dataLoc="features-list"
              frameClassName={`w-full ${featureLifecycleEnabled ? "" : "p-4"}`}
              globalFilter={searchQuery}
              initialState={{
                sorting: [{ id: "key", desc: true }],
              }}
              isLoading={isLoading}
              rowPropsGetter={(row) => ({
                "data-loc": row.original.key,
              })}
              variant={featureLifecycleEnabled ? "compact" : "default"}
              clientSideFiltering
            />
          </div>
        )}
      </div>
    </WhitePane>
  );
};

const getFirstLineContent = (descriptionValue: string) => {
  if (descriptionValue.includes("\n")) {
    return `${descriptionValue.split("\n")[0]}...`;
  }
  return descriptionValue.length > 50
    ? `${descriptionValue.slice(0, 43)}...`
    : descriptionValue;
};

export const DisplayDescriptionCell = <D extends object, V>({
  info,
  value: descriptionValue,
}: {
  info: CellContext<D, V>;
  value: string | null | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const needsPopover =
    descriptionValue &&
    (descriptionValue.length > 50 || descriptionValue.includes("\n"));

  const firstLineContent = getFirstLineContent(descriptionValue ?? "");

  const content = (
    <div className="line-clamp-1 overflow-hidden">
      <div
        //skipcq: JS-0440
        dangerouslySetInnerHTML={{
          __html: mdService.toHtml(
            needsPopover ? firstLineContent : (descriptionValue ?? ""),
          ),
        }}
        className="review-case-description [&>p]:m-0"
      />
    </div>
  );

  return needsPopover ? (
    <CustomPopover
      button={<Cell info={info}>{content}</Cell>}
      className="w-96 overflow-auto border-0"
      isOpen={isOpen}
      offsetX={0}
      placement="top"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {isOpen && (
        <div
          //skipcq: JS-0440
          dangerouslySetInnerHTML={{
            __html: mdService.toHtml(descriptionValue),
          }}
          className="review-case-description p-3"
        />
      )}
    </CustomPopover>
  ) : (
    <Cell info={info}>{content}</Cell>
  );
};
